import {
  Tr,
  Td,
  Text,
  HStack,
  Icon,
  Badge,
  List,
  ListItem,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
  CloseIcon,
  WarningIcon,
} from '@chakra-ui/icons';

import type { AvailableCarrier, CarrierQuote } from 'api/quote-marketplace';
import type { QuoteMarketplaceTheme } from '../../hooks';
import {
  CarrierCell,
  STICKY_LABEL_WIDTH,
  STICKY_REQUESTED_WIDTH,
} from './CarrierCell';

interface MarketMessagesSectionProps {
  availableCarriers: AvailableCarrier[];
  carrierQuotes: CarrierQuote[];
  selectedCarrierQuoteId: number | null;
  isExpanded: boolean;
  onToggle: () => void;
  theme: QuoteMarketplaceTheme;
}

export const MarketMessagesSection = ({
  availableCarriers,
  carrierQuotes,
  selectedCarrierQuoteId,
  isExpanded,
  onToggle,
  theme,
}: MarketMessagesSectionProps) => {
  const getQuoteForCarrier = (
    carrier: AvailableCarrier,
  ): CarrierQuote | undefined => {
    return carrierQuotes.find((q) => q.acturis_product === carrier.product);
  };

  const isCarrierSelected = (carrier: AvailableCarrier): boolean => {
    const quote = getQuoteForCarrier(carrier);
    return quote?.id === selectedCarrierQuoteId;
  };

  return (
    <>
      <Tr
        bg={theme.sectionHeaderBg}
        onClick={onToggle}
        cursor="pointer"
        _hover={{ bg: theme.hoverBg }}
      >
        <Td
          position="sticky"
          left={0}
          bg={theme.sectionHeaderBg}
          borderColor={theme.borderColor}
          py={3}
          zIndex={2}
        >
          <HStack spacing={2}>
            <Icon
              as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
              boxSize={4}
              color={theme.sectionHeaderColor}
            />
            <Text
              fontSize="sm"
              fontWeight="500"
              color={theme.sectionHeaderColor}
            >
              Market Messages
            </Text>
          </HStack>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        />
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          const quote = getQuoteForCarrier(carrier);
          const ratingNotes = quote?.rating_notes || [];
          const isDeclined = quote?.status === 'declined';

          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            >
              {isDeclined ? (
                <Badge colorScheme="red" fontSize="xs" px={2} py={1}>
                  <Icon as={CloseIcon} boxSize={2} />
                </Badge>
              ) : ratingNotes.length > 0 ? (
                <Badge colorScheme="yellow" fontSize="xs" px={2} py={1}>
                  <Icon as={WarningIcon} boxSize={3} />
                </Badge>
              ) : quote?.status === 'quoted' ? (
                <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                  <Icon as={CheckIcon} boxSize={2} />
                </Badge>
              ) : (
                <Text color={theme.labelColor} fontSize="sm">
                  -
                </Text>
              )}
            </CarrierCell>
          );
        })}
      </Tr>

      {isExpanded && (
        <Tr>
          <Td
            position="sticky"
            left={0}
            bg={theme.bgColor}
            borderColor={theme.borderColor}
            py={3}
            zIndex={2}
          />
          <Td
            bg={theme.requestedBg}
            borderColor={theme.borderColor}
            py={3}
            position="sticky"
            left={`${STICKY_LABEL_WIDTH}px`}
            zIndex={2}
            minW={`${STICKY_REQUESTED_WIDTH}px`}
          />
          {availableCarriers.map((carrier, index) => {
            const isSelected = isCarrierSelected(carrier);
            const quote = getQuoteForCarrier(carrier);
            const ratingNotes = quote?.rating_notes || [];
            const isDeclined = quote?.status === 'declined';
            const hasDeclineReason = isDeclined && quote?.decline_reason;
            const hasContent = hasDeclineReason || ratingNotes.length > 0;

            return (
              <CarrierCell
                key={carrier.carrier_id}
                isSelected={isSelected}
                theme={theme}
                minW="300px"
                verticalAlign="top"
                isSticky={index === 0 && isSelected}
              >
                {hasContent ? (
                  <>
                    {hasDeclineReason && (
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        mb={ratingNotes.length > 0 ? 2 : 0}
                      >
                        {quote.decline_reason}
                      </Text>
                    )}
                    {ratingNotes.length > 0 && (
                      <List spacing={2} styleType="disc" pl={4}>
                        {ratingNotes.map((note, idx) => (
                          <ListItem key={idx}>
                            <Text fontSize="xs" color={theme.labelColor}>
                              {note.description}
                            </Text>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                ) : (
                  <Text color={theme.labelColor} fontSize="sm">
                    -
                  </Text>
                )}
              </CarrierCell>
            );
          })}
        </Tr>
      )}
    </>
  );
};
